import { useEffect, useState } from "react";
import { LOGO_URL } from "../lib/assets";

// Module-level cache so we only process the source PNG once per session
let cachedTransparentSrc = null;
let inFlightPromise = null;

// Reads the logo PNG, makes all near-black pixels transparent, crops to the
// logo's actual bounding box, and returns a fresh transparent data URL.
// Runs in the browser only.
async function processLogo() {
  if (cachedTransparentSrc) return cachedTransparentSrc;
  if (inFlightPromise) return inFlightPromise;

  inFlightPromise = new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const imgData = ctx.getImageData(0, 0, w, h);
        const px = imgData.data;

        // Alpha-out near-black pixels (source canvas is solid black)
        const THRESHOLD = 42;
        for (let i = 0; i < px.length; i += 4) {
          const r = px[i];
          const g = px[i + 1];
          const b = px[i + 2];
          if (r < THRESHOLD && g < THRESHOLD && b < THRESHOLD) {
            px[i + 3] = 0;
          }
        }
        ctx.putImageData(imgData, 0, 0);

        // Find bounding box of remaining opaque pixels
        let minX = w, minY = h, maxX = 0, maxY = 0;
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            const a = px[(y * w + x) * 4 + 3];
            if (a > 8) {
              if (x < minX) minX = x;
              if (y < minY) minY = y;
              if (x > maxX) maxX = x;
              if (y > maxY) maxY = y;
            }
          }
        }
        if (minX >= maxX || minY >= maxY) {
          // Fallback: nothing detected — return the raw image
          resolve(LOGO_URL);
          return;
        }
        const cw = maxX - minX + 1;
        const ch = maxY - minY + 1;
        const cropped = document.createElement("canvas");
        cropped.width = cw;
        cropped.height = ch;
        const cctx = cropped.getContext("2d");
        cctx.drawImage(canvas, minX, minY, cw, ch, 0, 0, cw, ch);
        const dataUrl = cropped.toDataURL("image/png");
        cachedTransparentSrc = dataUrl;
        resolve(dataUrl);
      } catch (err) {
        // If the canvas gets tainted (CORS), fall back to raw source
        console.warn("Logo transparency processing failed:", err);
        resolve(LOGO_URL);
      }
    };
    img.onerror = () => {
      console.warn("Logo image failed to load, using raw source");
      resolve(LOGO_URL);
    };
    img.src = LOGO_URL;
  });

  return inFlightPromise;
}

// Renders the Cabo Unstoppable logo with the original dark canvas removed and
// cropped to the mark's bounding box. Preserves original design, typography,
// yellow brush, white lettering, proportions and aspect ratio exactly.
export const Logo = ({ className = "", size = "md" }) => {
  const [src, setSrc] = useState(cachedTransparentSrc);

  useEffect(() => {
    let cancelled = false;
    processLogo().then((url) => {
      if (!cancelled) setSrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Heights only — width auto-scales to the cropped logo's natural aspect
  // (roughly 5:7 portrait after cropping the empty canvas).
  const sizes = {
    sm: "h-9",
    md: "h-11 md:h-12",
    lg: "h-14",
    xl: "h-20 md:h-24",
  };

  return (
    <span
      role="img"
      aria-label="Cabo Unstoppable"
      data-testid="brand-logo"
      className={`inline-flex items-center ${sizes[size] || sizes.md} ${className}`}
    >
      {src && (
        <img
          src={src}
          alt="Cabo Unstoppable"
          draggable={false}
          className="block h-full w-auto object-contain select-none"
        />
      )}
    </span>
  );
};
